/**
 * Тонкие обёртки fetch для админ-эндпоинтов /api/admin/*.
 *
 * Server-side контракт — packages/server/src/admin-routes.ts.
 * Все запросы идут с credentials: 'include' и кидают Error на non-2xx;
 * для 403 страница админки покажет «Нет доступа».
 */

// ─── shared helpers ────────────────────────────────────────────────────

async function getJson<T>(path: string, params?: Record<string, string | undefined>): Promise<T> {
    const url = new URL(path, location.origin);
    if (params) {
        for (const [k, v] of Object.entries(params)) {
            if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
        }
    }
    const res = await fetch(url.pathname + url.search, { credentials: 'include' });
    if (!res.ok) throw new Error(`${path} ${res.status}: ${await res.text().catch(() => '')}`);
    return await res.json() as T;
}

async function sendJson<T>(method: 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown, params?: Record<string, string | undefined>): Promise<T> {
    const url = new URL(path, location.origin);
    if (params) {
        for (const [k, v] of Object.entries(params)) {
            if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
        }
    }
    const res = await fetch(url.pathname + url.search, {
        method,
        credentials: 'include',
        headers:     body ? { 'Content-Type': 'application/json' } : undefined,
        body:        body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new Error(`${path} ${res.status}: ${await res.text().catch(() => '')}`);
    return await res.json() as T;
}

// ─── overview ──────────────────────────────────────────────────────────

export interface UsageTotals {
    calls:            number;
    costUsd:          number;
    inputTokens:      number;
    outputTokens:     number;
    cacheWriteTokens: number;
    cacheReadTokens:  number;
}

export interface AdminOverview {
    users:   { total: number; admins: number };
    chat:    { sessionsTotal: number };
    storage: {
        snapshotsV2:   { count: number; bytes: number };
        repoCheckouts: { count: number; bytes: number };
        diffCache:     { count: number; bytes: number };
        llmCache:      { count: number; bytes: number };
    };
    usage: {
        last24h: UsageTotals;
        last7d:  UsageTotals;
        last30d: UsageTotals;
    };
}

export const fetchAdminOverview = (): Promise<AdminOverview> =>
    getJson<AdminOverview>('/api/admin/overview');

// ─── users ─────────────────────────────────────────────────────────────

export type UserRole = 'user' | 'admin';

export interface UserListItem {
    email:         string;
    name:          string | null;
    image:         string | null;
    role:          UserRole;
    firstSeen:     number;
    lastSeen:      number;
    sessionsCount: number;
    eventsCount:   number;
    lastActivity:  number | null;
}

export interface UserDetail {
    user: {
        email:     string;
        name:      string | null;
        image:     string | null;
        role:      UserRole;
        firstSeen: number;
        lastSeen:  number;
    };
    events: Array<{
        id:    number;
        user:  string;
        ts:    number;
        kind:  string;
        owner: string | null;
        repo:  string | null;
        meta:  unknown;
    }>;
    sessions: Array<{
        id:           string;
        owner:        string;
        repo:         string;
        user:         string;
        title:        string;
        createdAt:    number;
        updatedAt:    number;
        messageCount: number;
    }>;
}

export const fetchAdminUsers = (params: { search?: string; role?: UserRole; limit?: number; offset?: number } = {}): Promise<{ users: UserListItem[]; total: number }> =>
    getJson('/api/admin/users', {
        search: params.search,
        role:   params.role,
        limit:  params.limit  != null ? String(params.limit)  : undefined,
        offset: params.offset != null ? String(params.offset) : undefined,
    });

export const fetchAdminUser = (email: string): Promise<UserDetail> =>
    getJson<UserDetail>(`/api/admin/users/${encodeURIComponent(email)}`);

export const setUserRole = (email: string, role: UserRole): Promise<{ ok: true }> =>
    sendJson('POST', `/api/admin/users/${encodeURIComponent(email)}/role`, { role });

// ─── admins ────────────────────────────────────────────────────────────

export interface AdminsList {
    admins: Array<{
        email:     string;
        name:      string | null;
        image:     string | null;
        role:      UserRole;
        firstSeen: number;
        lastSeen:  number;
    }>;
    envBootstrap: string[];
}

export const fetchAdmins = (): Promise<AdminsList> => getJson('/api/admin/admins');

// ─── chats ─────────────────────────────────────────────────────────────

export interface ChatSessionRow {
    id:           string;
    owner:        string;
    repo:         string;
    user:         string;
    title:        string;
    createdAt:    number;
    updatedAt:    number;
    messageCount: number;
}

export interface ChatDetail {
    session: Omit<ChatSessionRow, 'messageCount'>;
    messages: Array<{
        id:        string;
        sessionId: string;
        role:      'user' | 'assistant';
        content:   string;
        createdAt: number;
    }>;
}

export const fetchAdminChats = (params: { user?: string; owner?: string; repo?: string; limit?: number; offset?: number } = {}): Promise<{ sessions: ChatSessionRow[]; total: number }> =>
    getJson('/api/admin/chats', {
        user:   params.user,
        owner:  params.owner,
        repo:   params.repo,
        limit:  params.limit  != null ? String(params.limit)  : undefined,
        offset: params.offset != null ? String(params.offset) : undefined,
    });

export const fetchAdminChat = (sessionId: string): Promise<ChatDetail> =>
    getJson(`/api/admin/chats/${encodeURIComponent(sessionId)}`);

export const deleteAdminChat = (sessionId: string): Promise<{ ok: true }> =>
    sendJson('DELETE', `/api/admin/chats/${encodeURIComponent(sessionId)}`);

// ─── usage ─────────────────────────────────────────────────────────────

export interface UsageGroup { key: string; totals: UsageTotals; }

export interface UsageRow {
    tsServer:         string;
    user:             string;
    owner:            string;
    repo:             string;
    model:            string;
    kind:             'aspect' | 'rollup' | 'entity' | 'chat';
    inputTokens:      number;
    outputTokens:     number;
    cacheWriteTokens: number;
    cacheReadTokens:  number;
    costUsd:          number;
    latencyMs:        number;
}

export interface UsageResponse {
    totals:  UsageTotals;
    byKind:  UsageGroup[];
    byUser:  UsageGroup[];
    byModel: UsageGroup[];
    byOwner: UsageGroup[];
    split:   { chat: UsageTotals; compare: UsageTotals };
    rows:    UsageRow[];
}

export const fetchAdminUsage = (params: { user?: string; owner?: string; kind?: UsageRow['kind']; from?: string; to?: string; limit?: number } = {}): Promise<UsageResponse> =>
    getJson('/api/admin/usage', {
        user:  params.user,
        owner: params.owner,
        kind:  params.kind,
        from:  params.from,
        to:    params.to,
        limit: params.limit != null ? String(params.limit) : undefined,
    });

// ─── cache ─────────────────────────────────────────────────────────────

export interface SnapshotCacheEntry {
    owner:   string;
    repo:    string;
    sha:     string;
    subpath: string;
    size:    number;
    mtimeMs: number;
}

export interface RepoCheckoutEntry {
    owner:   string;
    repo:    string;
    kind:    'branch' | 'sha';
    ref:     string;
    size:    number;
    mtimeMs: number;
}

export interface DiffCacheEntry {
    owner:   string;
    repo:    string;
    version: string;
    baseSha: string;
    headSha: string;
    size:    number;
    mtimeMs: number;
}

export const fetchSnapshotsCache = (filter: { owner?: string; repo?: string } = {}): Promise<{ entries: SnapshotCacheEntry[] }> =>
    getJson('/api/admin/cache/snapshots', filter);

export const fetchReposCache = (filter: { owner?: string; repo?: string } = {}): Promise<{ entries: RepoCheckoutEntry[] }> =>
    getJson('/api/admin/cache/repos', filter);

export const fetchDiffsCache = (filter: { owner?: string; repo?: string } = {}): Promise<{ entries: DiffCacheEntry[] }> =>
    getJson('/api/admin/cache/diffs', filter);

export const purgeSnapshotsCache = (params: { all?: boolean; owner?: string; repo?: string; sha?: string }): Promise<{ ok: true; removed: number }> =>
    sendJson('DELETE', '/api/admin/cache/snapshots', undefined, {
        all:   params.all ? '1' : undefined,
        owner: params.owner,
        repo:  params.repo,
        sha:   params.sha,
    });

export const purgeReposCache = (params: { all?: boolean; owner?: string; repo?: string; ref?: string; kind?: 'branch' | 'sha' }): Promise<{ ok: true; removed: number }> =>
    sendJson('DELETE', '/api/admin/cache/repos', undefined, {
        all:   params.all ? '1' : undefined,
        owner: params.owner,
        repo:  params.repo,
        ref:   params.ref,
        kind:  params.kind,
    });

export const purgeDiffsCache = (params: { all?: boolean; owner?: string; repo?: string }): Promise<{ ok: true; removed: number }> =>
    sendJson('DELETE', '/api/admin/cache/diffs', undefined, {
        all:   params.all ? '1' : undefined,
        owner: params.owner,
        repo:  params.repo,
    });

export const rebuildSnapshot = (params: { owner: string; repo: string; sha: string; ref?: string }): Promise<{ ok: true; meta: unknown }> =>
    sendJson('POST', '/api/admin/cache/snapshots/rebuild', params);

// ─── chat settings ─────────────────────────────────────────────────────

/** Бюджет max_tokens per label; пустая строка = без ограничения. */
export interface MaxTokensSettings {
    aspect:  string;
    rollup:  string;
    entity:  string;
    summary: string;
}

export interface ChatSettings {
    systemPrompt: string;
    model:        string;
    /** Модель среза карточки; пусто = «как у чата». */
    summaryModel:        string;
    summarySystemPrompt: string;
    /** Бюджеты max_tokens per label (сравнение: aspect/rollup/entity; срез: summary). Пусто = без ограничения. */
    maxTokens: MaxTokensSettings;
    defaults: { systemPrompt: string; model: string; summarySystemPrompt: string };
    updated: {
        systemPrompt:        { at: number; by: string | null } | null;
        model:               { at: number; by: string | null } | null;
        summaryModel:        { at: number; by: string | null } | null;
        summarySystemPrompt: { at: number; by: string | null } | null;
        maxTokens: Record<keyof MaxTokensSettings, { at: number; by: string | null } | null>;
    };
}

export interface ChatModelInfo {
    id:            string;
    name:          string;
    context:       number;
    promptUsd:     number;
    completionUsd: number;
    created:       number;
    recommended:   boolean;
}

export const fetchChatSettings = (): Promise<ChatSettings> => getJson('/api/admin/chat-settings');

export const fetchOpenrouterModels = (): Promise<{ models: ChatModelInfo[] }> =>
    getJson('/api/admin/openrouter-models');

// ─── tool usage (chat-tools.jsonl) ─────────────────────────────────────

export interface ToolUsageRow {
    tool:       string;
    calls:      number;
    errors:     number;
    avgMs:      number;
    p50Ms:      number;
    maxMs:      number;
    avgBytes:   number;
    totalBytes: number;
}

export interface ToolUsageEntry {
    ts:          string;
    user:        string;
    owner:       string;
    repo:        string;
    model:       string;
    sessionId:   string | null;
    tool:        string;
    input:       string;
    ok:          boolean;
    error?:      string;
    outputBytes: number;
    durationMs?: number;
}

export interface ToolUsageReport {
    days:   number;
    totals: { calls: number; errors: number; totalBytes: number; sessions: number };
    tools:  ToolUsageRow[];
    rows:   ToolUsageEntry[];
}

export const fetchToolUsage = (days: number): Promise<ToolUsageReport> =>
    getJson(`/api/admin/tool-usage?days=${days}`);

type ChatSettingsKey = 'systemPrompt' | 'model' | 'summaryModel' | 'summarySystemPrompt'
    | 'maxTokens.aspect' | 'maxTokens.rollup' | 'maxTokens.entity' | 'maxTokens.summary';

export const updateChatSettings = (
    patch: {
        systemPrompt?: string; model?: string; summaryModel?: string; summarySystemPrompt?: string;
        maxTokens?: Partial<MaxTokensSettings>;
    },
): Promise<{ ok: true; changed: string[] }> =>
    sendJson('PUT', '/api/admin/chat-settings', patch);

export const resetChatSettings = (keys: ChatSettingsKey[]): Promise<{ ok: true; reset: string[] }> =>
    sendJson('POST', '/api/admin/chat-settings/reset', { keys });
